import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Pause as PauseIcon,
  PlayArrow as PlayArrowIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AutomatedMessageModal from '../components/AutomatedMessageModal';
import CancelButton from '../components/CancelButton';
import Loading from '../components/Loading';
import NutritionTracker from '../components/NutritionTracker';
import WeightTracker from '../components/WeightTracker';
import { Appointment, appointmentApi } from '../services/appointmentApi';
import {
  AutomatedMessage,
  automatedMessageApi,
  CreateAutomatedMessageRequest,
  getRuleDescription,
  getTriggerTypeIcon,
  triggerTypeLabels,
} from '../services/automatedMessageApi';
import { Client, clientApi } from '../services/clientApi';
import { DietProgram, dietProgramApi } from '../services/dietProgramApi';
import { colors } from '../theme';

const DietitianClientDetailPage: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const { dietitianId, dietitian } = useAuth();
  const [client, setClient] = useState<Client | null>(null);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [dietPrograms, setDietPrograms] = useState<DietProgram[]>([]);
  const [automatedMessages, setAutomatedMessages] = useState<AutomatedMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [selectedDate] = useState<Date>(new Date());
  
  // Automated Messages state
  const [automatedMessageModalOpen, setAutomatedMessageModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<AutomatedMessage | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const statusChipStyle = useMemo(() => {
    // Placeholder: if later a status is added, keep consistent styling
    return { bgcolor: 'rgba(255,255,255,0.25)' };
  }, []);

  const loadAutomatedMessages = useCallback(async () => {
    if (!clientId) return;
    try {
      const rules = await automatedMessageApi.getByClient(clientId);
      setAutomatedMessages(rules);
    } catch (err) {
      console.error('Error fetching automated messages:', err);
    }
  }, [clientId]);

  const handleCreateRule = async (data: CreateAutomatedMessageRequest) => {
    try {
      if (editingRule) {
        await automatedMessageApi.update(editingRule.id, data);
        setSnackbar({
          open: true,
          message: 'Kural başarıyla güncellendi',
          severity: 'success',
        });
      } else {
        await automatedMessageApi.create(data);
        setSnackbar({
          open: true,
          message: 'Kural başarıyla oluşturuldu',
          severity: 'success',
        });
      }
      loadAutomatedMessages();
      setEditingRule(null);
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Kural kaydedilirken hata oluştu',
        severity: 'error',
      });
      throw error;
    }
  };

  const handleToggleRule = async (id: string) => {
    try {
      await automatedMessageApi.toggle(id);
      loadAutomatedMessages();
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Kural durumu değiştirilemedi',
        severity: 'error',
      });
    }
  };

  const handleDeleteRule = async () => {
    if (!ruleToDelete) return;

    try {
      await automatedMessageApi.delete(ruleToDelete);
      setSnackbar({
        open: true,
        message: 'Kural başarıyla silindi',
        severity: 'success',
      });
      loadAutomatedMessages();
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Kural silinirken hata oluştu',
        severity: 'error',
      });
    } finally {
      setDeleteDialogOpen(false);
      setRuleToDelete(null);
    }
  };

  const handleSendNow = async (ruleId: string) => {
    try {
      const result = await automatedMessageApi.sendNow(ruleId);
      setSnackbar({
        open: true,
        message: result.message,
        severity: 'success',
      });
      loadAutomatedMessages();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Mesaj gönderilemedi',
        severity: 'error',
      });
    }
  };

  useEffect(() => {
    const fetchClientData = async () => {
      if (!clientId) return;

      try {
        setLoading(true);
        const [clientData, appointmentsData, programsData, automatedMessagesData] = await Promise.all([
          clientApi.getClientById(clientId),
          appointmentApi.getAppointmentsByClient(clientId),
          dietProgramApi.getDietProgramsByClient(clientId),
          automatedMessageApi.getByClient(clientId),
        ]);

        setClient(clientData);
        setAppointments(appointmentsData);
        setDietPrograms(programsData);
        setAutomatedMessages(automatedMessagesData);
      } catch (err) {
        console.error('Error fetching client data:', err);
        setError('Danışan bilgileri yüklenirken hata oluştu');
      } finally {
        setLoading(false);
      }
    };

    fetchClientData();
  }, [clientId]);

  if (loading) {
    return (
      <Loading
        size="large"
        variant="wave"
        mode="inline"
        message="Danışan bilgileri yükleniyor..."
      />
    );
  }

  if (error || !client) {
    return (
      <Paper elevation={2} sx={{ p: 4, pl: 0, textAlign: 'center' }}>
        <Typography variant="h6" color="error" gutterBottom>
          {error || 'Danışan bulunamadı'}
        </Typography>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/dietitian/clients')}
          sx={{
            mt: 2,
            width: '250px',
            bgcolor: colors.primary.main,
            '&:hover': { bgcolor: colors.hover.primary },
          }}
        >
          Danışanlar Listesine Dön
        </Button>
      </Paper>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100vw',
        overflowX: 'auto',
        overflowY: 'hidden',
      }}
    >
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          mb: 1.5,
          borderRadius: 1.5,
          background: 'linear-gradient(135deg, #43A047 0%, #2E7D32 100%)',
          color: 'white',
          boxShadow: '0 4px 12px rgba(46, 125, 50, 0.25)',
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={0.5}
          gap={0.75}
        >
          <Box display="flex" alignItems="center" gap={0.75}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/dietitian/clients')}
              sx={{
                color: 'white',
                textTransform: 'none',
                px: 0.5,
                minHeight: 32,
                minWidth: 36,
                borderRadius: 1.25,
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.12)',
                },
              }}
            ></Button>
            <Box
              display="flex"
              alignItems="center"
              gap={0.5}
              sx={{ minWidth: 140 }}
            >
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{ color: 'white', lineHeight: 1.2 }}
              >
                {client.firstName} {client.lastName}
              </Typography>
            </Box>
          </Box>
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            sx={{ minWidth: 110, justifyContent: 'flex-end' }}
          >
            {client.goal && (
              <Chip
                label={client.goal}
                size="small"
                sx={{
                  height: 22,
                  borderRadius: 999,
                  bgcolor: 'rgba(255,255,255,0.18)',
                  color: 'white',
                  fontWeight: 600,
                  px: 1,
                }}
              />
            )}
            {client.age && (
              <Chip
                label={`${client.age} yaş`}
                size="small"
                sx={{
                  ...statusChipStyle,
                  color: 'white',
                  fontWeight: 700,
                  letterSpacing: 0.2,
                  height: 26,
                  borderRadius: 999,
                  px: 2,
                }}
              />
            )}
          </Box>
        </Box>
      </Paper>

      {/* Tabs */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          width: '100%',
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: `2px solid ${colors.border.light}`,
            bgcolor: colors.background.light,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '1rem',
              minHeight: 60,
              color: colors.text.secondary,
              '&:hover': {
                bgcolor: colors.primary.main,
                color: 'white',
              },
              '&.Mui-selected': {
                color: colors.primary.main,
                '&:hover': {
                  bgcolor: colors.primary.main,
                  color: 'white',
                },
              },
            },
            '& .MuiTabs-indicator': {
              height: 3,
              bgcolor: colors.primary.main,
            },
            '& .MuiTabs-scrollButtons': {
              color: colors.primary.main,
              '&.Mui-disabled': {
                opacity: 0.3,
              },
            },
          }}
        >
          <Tab label="📋 Genel Bilgiler" />
          <Tab label="🍽️ Yemek Takibi" />
          <Tab label="⚖️ Kilo Takibi" />
          <Tab label="📅 Randevular" />
          <Tab label="📊 Diyet Programları" />
          <Tab label="🤖 Otomatik Mesajlar" />
        </Tabs>

        <Box sx={{ p: 3, minHeight: '600px', width: '100%' }}>
          {/* Genel Bilgiler */}
          {activeTab === 0 && (
            <Box
              sx={{
                animation: 'fadeIn 0.3s ease-in',
                '@keyframes fadeIn': {
                  from: { opacity: 0, transform: 'translateY(10px)' },
                  to: { opacity: 1, transform: 'translateY(0)' },
                },
                minHeight: '500px',
              }}
            >
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Danışan Bilgileri
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: 2,
                  mt: 2,
                }}
              >
                <Paper sx={{ p: 2, bgcolor: colors.background.light }}>
                  <Typography variant="caption" color="text.secondary">
                    Boy
                  </Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {client.height && client.height > 0 ? `${client.height} cm` : '-'}
                  </Typography>
                </Paper>
                {client.goal && (
                  <Paper sx={{ p: 2, bgcolor: colors.background.light }}>
                    <Typography variant="caption" color="text.secondary">
                      Hedef
                    </Typography>
                    <Typography variant="h6" fontWeight={600}>
                      {client.goal}
                    </Typography>
                  </Paper>
                )}
                {client.dietitianEmail && (
                  <Paper sx={{ p: 2, bgcolor: colors.background.light }}>
                    <Typography variant="caption" color="text.secondary">
                      Diyetisyen
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {client.dietitianEmail}
                    </Typography>
                  </Paper>
                )}
              </Box>
              {client.notes && (
                <Paper sx={{ p: 2, mt: 3, bgcolor: colors.background.light }}>
                  <Typography variant="caption" color="text.secondary">
                    Notlar
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 1 }}>
                    {client.notes}
                  </Typography>
                </Paper>
              )}
            </Box>
          )}

          {/* Yemek Takibi */}
          {activeTab === 1 && (
            <Box
              sx={{
                animation: 'fadeIn 0.3s ease-in',
                '@keyframes fadeIn': {
                  from: { opacity: 0, transform: 'translateY(10px)' },
                  to: { opacity: 1, transform: 'translateY(0)' },
                },
                minHeight: '600px',
                '& > div': {
                  minHeight: '600px',
                },
              }}
            >
              <NutritionTracker
                clientId={clientId!}
                clientName={`${client.firstName} ${client.lastName}`}
                selectedDate={selectedDate}
                isDietitian={false}
                canAddEditMeals={false}
                client={client}
              />
            </Box>
          )}

          {/* Kilo Takibi */}
          {activeTab === 2 && (
            <Box
              sx={{
                animation: 'fadeIn 0.3s ease-in',
                '@keyframes fadeIn': {
                  from: { opacity: 0, transform: 'translateY(10px)' },
                  to: { opacity: 1, transform: 'translateY(0)' },
                },
                minHeight: '600px',
                '& > div': {
                  minHeight: '600px',
                },
              }}
            >
              <WeightTracker
                clientId={clientId!}
                clientName={`${client.firstName} ${client.lastName}`}
                client={client}
                isDietitian={true}
                dietitianId={dietitianId ?? undefined}
                dietitianName={dietitian ? `${dietitian.firstName} ${dietitian.lastName}` : undefined}
              />
            </Box>
          )}

          {/* Randevular */}
          {activeTab === 3 && (
            <Box
              sx={{
                animation: 'fadeIn 0.3s ease-in',
                '@keyframes fadeIn': {
                  from: { opacity: 0, transform: 'translateY(10px)' },
                  to: { opacity: 1, transform: 'translateY(0)' },
                },
              }}
            >
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Randevular
              </Typography>
              {appointments.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 2 }}
                >
                  Henüz randevu bulunmuyor.
                </Typography>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    mt: 2,
                  }}
                >
                  {appointments
                    .sort(
                      (a, b) =>
                        new Date(b.appointmentDate).getTime() -
                        new Date(a.appointmentDate).getTime()
                    )
                    .map((apt) => (
                      <Paper
                        key={apt.id}
                        sx={{ p: 2, bgcolor: colors.background.light }}
                      >
                        <Typography variant="subtitle1" fontWeight={600}>
                          {new Date(apt.appointmentDate).toLocaleDateString(
                            'tr-TR'
                          )}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {apt.startTime} - {apt.endTime} • {apt.type} •{' '}
                          {apt.status}
                        </Typography>
                        {apt.notes && (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            {apt.notes}
                          </Typography>
                        )}
                      </Paper>
                    ))}
                </Box>
              )}
            </Box>
          )}

          {/* Diyet Programları */}
          {activeTab === 4 && (
            <Box
              sx={{
                animation: 'fadeIn 0.3s ease-in',
                '@keyframes fadeIn': {
                  from: { opacity: 0, transform: 'translateY(10px)' },
                  to: { opacity: 1, transform: 'translateY(0)' },
                },
              }}
            >
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Diyet Programları
              </Typography>
              {dietPrograms.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 2 }}
                >
                  Henüz diyet programı bulunmuyor.
                </Typography>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    mt: 2,
                  }}
                >
                  {dietPrograms
                    .sort(
                      (a, b) =>
                        new Date(b.createdAt).getTime() -
                        new Date(a.createdAt).getTime()
                    )
                    .map((program) => (
                      <Paper
                        key={program.id}
                        onClick={() => navigate(`/dietitian/programs/${program.id}`)}
                        sx={{ 
                          p: 2, 
                          bgcolor: colors.background.light,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          '&:hover': {
                            bgcolor: colors.hover.background,
                            borderColor: colors.primary.main,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            transform: 'translateY(-2px)',
                          },
                          border: `1px solid ${colors.border.light}`,
                        }}
                      >
                        <Typography variant="h6" fontWeight={600}>
                          {program.programName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {program.programType} • {program.status}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {new Date(program.startDate).toLocaleDateString(
                            'tr-TR'
                          )}{' '}
                          -{' '}
                          {new Date(program.endDate).toLocaleDateString(
                            'tr-TR'
                          )}
                        </Typography>
                        {program.description && (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            {program.description}
                          </Typography>
                        )}
                      </Paper>
                    ))}
                </Box>
              )}
            </Box>
          )}

          {/* Otomatik Mesajlar */}
          {activeTab === 5 && (
            <Box
              sx={{
                animation: 'fadeIn 0.3s ease-in',
                '@keyframes fadeIn': {
                  from: { opacity: 0, transform: 'translateY(10px)' },
                  to: { opacity: 1, transform: 'translateY(0)' },
                },
              }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={3}
              >
                <Typography variant="h6" fontWeight={700}>
                  🤖 Otomatik Mesaj Kuralları
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setEditingRule(null);
                    setAutomatedMessageModalOpen(true);
                  }}
                  sx={{
                    bgcolor: colors.primary.main,
                    width:'fit-content',
                    '&:hover': { bgcolor: colors.hover.primary },
                  }}
                >
                  Yeni Kural
                </Button>
              </Box>

              {automatedMessages.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <Typography
                    variant="h6"
                    color="text.secondary"
                    gutterBottom
                  >
                    Bu danışan için otomatik mesaj kuralı yok
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    Hatırlatmalar, motivasyon mesajları ve daha fazlası için
                    kural oluşturun
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setEditingRule(null);
                      setAutomatedMessageModalOpen(true);
                    }}
                    sx={{
                      borderColor: colors.primary.main,
                      color: colors.primary.main,
                    }}
                  >
                    İlk Kuralı Oluştur
                  </Button>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                    gap: 2,
                  }}
                >
                  {automatedMessages.map((rule) => (
                    <Card
                      key={rule.id}
                      sx={{
                        borderRadius: 2,
                        border: `1px solid ${
                          rule.isActive
                            ? colors.primary.light
                            : colors.border.light
                        }`,
                        opacity: rule.isActive ? 1 : 0.7,
                        transition: 'all 0.2s',
                        '&:hover': {
                          boxShadow: colors.shadows.cardHover,
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                        <CardContent>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="flex-start"
                            mb={2}
                          >
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="h5">
                                {getTriggerTypeIcon(rule.triggerType)}
                              </Typography>
                              <Box>
                                <Typography
                                  variant="subtitle1"
                                  fontWeight={600}
                                >
                                  {rule.title}
                                </Typography>
                                <Chip
                                  label={
                                    triggerTypeLabels[rule.triggerType] ||
                                    rule.triggerType
                                  }
                                  size="small"
                                  sx={{
                                    bgcolor: colors.alpha.primary10,
                                    color: colors.primary.dark,
                                    fontWeight: 500,
                                  }}
                                />
                              </Box>
                            </Box>
                            <Chip
                              label={rule.isActive ? 'Aktif' : 'Pasif'}
                              size="small"
                              sx={{
                                bgcolor: rule.isActive
                                  ? colors.status.success
                                  : colors.text.disabled,
                                color: 'white',
                              }}
                            />
                          </Box>

                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              mb: 2,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {rule.content}
                          </Typography>

                          <Box
                            sx={{
                              p: 1.5,
                              bgcolor: colors.background.light,
                              borderRadius: 1,
                              mb: 2,
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              📋 {getRuleDescription(rule)}
                            </Typography>
                            {rule.lastSentAt && (
                              <>
                                <br />
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  📨 Son gönderim:{' '}
                                  {new Date(
                                    rule.lastSentAt
                                  ).toLocaleDateString('tr-TR')}
                                </Typography>
                              </>
                            )}
                            <br />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              📊 Toplam {rule.totalSentCount} mesaj gönderildi
                            </Typography>
                          </Box>

                          <Box
                            display="flex"
                            justifyContent="flex-end"
                            gap={1}
                          >
                            <Tooltip
                              title={
                                rule.isActive ? 'Duraklat' : 'Aktifleştir'
                              }
                            >
                              <IconButton
                                size="small"
                                onClick={() => handleToggleRule(rule.id)}
                                sx={{
                                  color: rule.isActive
                                    ? colors.status.warning.main
                                    : colors.status.success,
                                }}
                              >
                                {rule.isActive ? (
                                  <PauseIcon />
                                ) : (
                                  <PlayArrowIcon />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Şimdi Gönder">
                              <IconButton
                                size="small"
                                onClick={() => handleSendNow(rule.id)}
                                sx={{ color: colors.status.success }}
                              >
                                <SendIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Düzenle">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setEditingRule(rule);
                                  setAutomatedMessageModalOpen(true);
                                }}
                                sx={{ color: colors.primary.main }}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Sil">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setRuleToDelete(rule.id);
                                  setDeleteDialogOpen(true);
                                }}
                                sx={{ color: colors.status.error }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Paper>

      {/* Automated Message Modal */}
      <AutomatedMessageModal
        open={automatedMessageModalOpen}
        onClose={() => {
          setAutomatedMessageModalOpen(false);
          setEditingRule(null);
        }}
        onSave={handleCreateRule}
        editingRule={editingRule}
        preSelectedClientId={clientId}
        preSelectedClientName={client ? `${client.firstName} ${client.lastName}` : undefined}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Kuralı Sil</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Bu otomatik mesaj kuralını silmek istediğinizden emin misiniz? Bu
            işlem geri alınamaz.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <CancelButton onClick={() => setDeleteDialogOpen(false)} />
          <Button onClick={handleDeleteRule} color="error" variant="contained">
            Sil
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DietitianClientDetailPage;
